import {
  createElement,
  useMemo,
  type ComponentPropsWithoutRef,
  type ElementType,
} from 'react';
import DOMPurify from 'dompurify';

type SafeHTMLProps<T extends ElementType> = {
  as?: T;
  html?: string | null;
  addRelNoopener?: boolean;
} & Omit<ComponentPropsWithoutRef<T>, 'children' | 'dangerouslySetInnerHTML'>;

const addNoopenerToBlankTargets = (html: string) => html.replace(
  /<a\b([^>]*?)\btarget=(["']?)_blank\2([^>]*?)>/gi,
  (_match, beforeTarget, _quote, afterTarget) => {
    if (/rel=/i.test(beforeTarget + afterTarget)) {
      return `<a${beforeTarget}target="_blank"${afterTarget}>`;
    }

    return `<a${beforeTarget}target="_blank" rel="noopener noreferrer"${afterTarget}>`;
  },
);

function SafeHTML<T extends ElementType = 'span'>({
  as,
  html,
  addRelNoopener = true,
  ...rest
}: SafeHTMLProps<T>) {
  const Component = as || 'span';

  const sanitizedHtml = useMemo(() => {
    const cleanHtml = DOMPurify.sanitize(html || '', {
      USE_PROFILES: { html: true },
    });

    return addRelNoopener ? addNoopenerToBlankTargets(cleanHtml) : cleanHtml;
  }, [addRelNoopener, html]);

  return createElement(Component, {
    ...rest,
    dangerouslySetInnerHTML: { __html: sanitizedHtml },
  });
}

export default SafeHTML;
